import classNames from 'classnames'
import { useState } from 'react'

import { navigateTo } from '../../../../../common/frontend/route-utils.js'
import { t } from '../../../../../common/i18n.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { PortalConfirmDialog } from '../../../../components/portal-confirm-dialog/portal-confirm-dialog.js'
import { ProfileBanner } from '../../../../components/profile-banner/profile-banner.js'
import { Skeleton } from '../../../../components/skeleton/skeleton.js'
import { themeLabels } from '../../../../hero-data.js'
import { getDisplayName, getStatusLabel } from '../../utils/format.js'
import type { EmployeeProfileViewProps, TabId } from './employee-profile-types.js'
import './employee-profile-view.css'
import { DataTab } from './tabs/data-tab.js'
import { DocumentFoldersSection } from './tabs/document-folders-section.js'
import { ProfileBalanceTimeTab } from './tabs/profile-balancetime-tab.js'
import { ProfileProjectsTab } from './tabs/profile-projects-tab.js'
import { SettingsTab } from './tabs/settings-tab.js'
import { TimeAwayTab } from './tabs/time-away/time-away-tab.js'

// Re-export types for consumers that import from this file
export type {
    CompensationData,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationRecord,
    CompensationType,
    ContractRecord,
    CustomFieldWithValue,
    EmergencyContact,
    EmployeeDocument,
    EmployeeProfileData,
    EmployeeProfileViewProps,
    EmploymentHistoryRecord,
    EmploymentRecordInput,
    EmploymentRefData,
    ExperienceEntry,
    ExperienceInput,
    LeaveRequest,
    LeaveSettlement,
    ProfileLeaveBalance,
    WorkingPatternHistoryRecord,
} from './employee-profile-types.js'

// Map the current + legacy `?tab=` values onto the three consolidated tabs, so
// old links (e.g. ?tab=personal, ?tab=integrations) still land in the right place.
const TAB_BY_QUERY: Record<string, TabId> = {
    data: 'data',
    overview: 'data',
    personal: 'data',
    'employment-history': 'data',
    'job-pay': 'data',
    signature: 'data',
    'time-away': 'time-away',
    settings: 'settings',
    integrations: 'settings',
    // Admin-only tabs on another employee's profile (mirror the /me sections).
    documents: 'documents',
    worktime: 'worktime',
    projects: 'projects',
}

const resolveTab = (raw: string | null): TabId => (raw ? (TAB_BY_QUERY[raw] ?? 'data') : 'data')

export const EmployeeProfileView = ({
    employee,
    emergencyContacts,

    leaveBalances,
    leaveRequests,
    leaveSettlement,
    companyHolidays,
    compensationData,
    compensationLineItems,
    employmentHistory,
    workingPatterns,
    customFields,
    documents,
    isLoading,
    isSelf,
    isAdmin,
    view,
    lang,
    onSelectLanguage,
    themePreference,
    onSelectTheme,
    onCreateCompensation,
    onUpdateCompensation,
    onRevealCompensation,
    onUpdateEmploymentDates,
    employmentRefData,
    onCreateEmploymentRecord,
    onUpdatePersonal,
    canOffboardUsers,
    onChangeStatus,
    onOffboard,
    onSaveCustomFields,
    onAddEmergencyContact,
    onUpdateEmergencyContact,
    onDeleteEmergencyContact,
    experiences,
    onAddExperience,
    onUpdateExperience,
    onDeleteExperience,
    onAddDocument,
    onUpdateDocument,
    onDeleteDocument,
    onDeleteCompensation,
    compensationTypes,
    onCreateLineItem,
    onUpdateLineItem,
    onDeleteLineItem,
    onDeleteEmploymentRecord,
    onWorkingPatternsRefresh,
    onAvatarChange,
    onAvatarRemove,
    onNavigate,
    onBookTimeAway,
    onCancelTimeAway,
    onEditTimeAway,
    onDecideTimeAway,
    onDecideCancellation,
    onLeaveDataChanged,
    showSignature,
    locationChange,
    chromeless,
    activeSection,
}: EmployeeProfileViewProps) => {
    // /me passes the section as a prop; /people/:id falls back to the ?tab= query.
    const activeTab: TabId =
        activeSection ?? resolveTab(new URLSearchParams(window.location.search).get('tab'))

    const handleTabChange = (tabId: TabId) => {
        const url = new URL(window.location.href)
        url.searchParams.set('tab', tabId)
        navigateTo(url.pathname + url.search)
    }

    // Confirm dialog state (shared across all tabs via requestConfirm callback)
    const [pendingConfirm, setPendingConfirm] = useState<{
        message: string
        onConfirm: () => void
        options?: { confirmLabel?: string; variant?: 'danger' | 'primary' }
    } | null>(null)

    const ph = t.portal

    if (isLoading || !employee) {
        return (
            <div className="portal-profile__skeleton">
                <Skeleton count={6} height={56} />
            </div>
        )
    }

    const emp = employee.employment
    // Per-target capabilities resolved server-side — tab visibility gates on
    // these instead of role flags so the role editor's grants take effect.
    const cap = employee.viewer

    const statusLabel = getStatusLabel(employee.status, lang)

    // Worktime/projects reuse the playtime views (need the app `view` handle) and
    // are individually gated per role; they only appear on /people/:id (never
    // /me, which is chromeless and routes them itself).
    const canSeeWorktime = (cap?.canViewWorktime ?? false) && !!view
    const canSeeProjects = (cap?.canViewProjects ?? false) && !!view
    const tabs: { id: TabId; label: string; visible: boolean }[] = [
        { id: 'data', label: ph.profile.dataTab(lang), visible: true },
        {
            id: 'time-away',
            label: ph.profile.timeAwayTab(lang),
            visible: cap?.canViewTimeAway ?? false,
        },
        { id: 'worktime', label: ph.nav.worktime(lang), visible: canSeeWorktime },
        { id: 'projects', label: ph.profile.projectsTab(lang), visible: canSeeProjects },
        {
            id: 'documents',
            label: ph.documents.title(lang),
            // Tab visibility follows doc-type VIEW (managers see it read-only);
            // add/edit/delete inside follow canManageDocuments (doc-type edit).
            visible: cap?.canViewDocuments ?? false,
        },
        // Settings (language, API keys, notifications) are personal — only on your own profile.
        { id: 'settings', label: ph.profile.settingsTab(lang), visible: isSelf },
    ]

    // Fall back to the default tab when the requested one isn't visible here
    // (e.g. a hand-edited ?tab=settings on someone else's profile).
    const activeTabId: TabId = tabs.some((tab) => tab.visible && tab.id === activeTab)
        ? activeTab
        : 'data'

    const requestConfirm = (
        message: string,
        onConfirm: () => void,
        options?: { confirmLabel?: string; variant?: 'danger' | 'primary' },
    ) => {
        setPendingConfirm({ message, onConfirm, options })
    }

    const tabStrip = (
        <div className="portal-profile__tabs">
            {tabs
                .filter((tab) => tab.visible)
                .map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={classNames(
                            'portal-profile__tab',
                            activeTabId === tab.id && 'is-active',
                        )}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
        </div>
    )

    const tabContent = (
        <>
            {activeTabId === 'data' && (
                <DataTab
                    employee={employee}
                    emergencyContacts={emergencyContacts}
                    compensationData={compensationData}
                    compensationLineItems={compensationLineItems}
                    employmentHistory={employmentHistory}
                    workingPatterns={workingPatterns}
                    customFields={customFields}
                    isAdmin={isAdmin}
                    showSignature={Boolean(showSignature)}
                    lang={lang}
                    requestConfirm={requestConfirm}
                    onCreateCompensation={onCreateCompensation}
                    onUpdateCompensation={onUpdateCompensation}
                    onRevealCompensation={onRevealCompensation}
                    onUpdateEmploymentDates={onUpdateEmploymentDates}
                    employmentRefData={employmentRefData}
                    onCreateEmploymentRecord={onCreateEmploymentRecord}
                    locationChange={locationChange}
                    onUpdatePersonal={onUpdatePersonal}
                    canOffboardUsers={canOffboardUsers}
                    onChangeStatus={onChangeStatus}
                    onOffboard={onOffboard}
                    onSaveCustomFields={onSaveCustomFields}
                    onAddEmergencyContact={onAddEmergencyContact}
                    onUpdateEmergencyContact={onUpdateEmergencyContact}
                    onDeleteEmergencyContact={onDeleteEmergencyContact}
                    experiences={experiences}
                    onAddExperience={onAddExperience}
                    onUpdateExperience={onUpdateExperience}
                    onDeleteExperience={onDeleteExperience}
                    onDeleteCompensation={onDeleteCompensation}
                    compensationTypes={compensationTypes}
                    onCreateLineItem={onCreateLineItem}
                    onUpdateLineItem={onUpdateLineItem}
                    onDeleteLineItem={onDeleteLineItem}
                    onDeleteEmploymentRecord={onDeleteEmploymentRecord}
                    onWorkingPatternsRefresh={onWorkingPatternsRefresh}
                />
            )}

            {activeTabId === 'time-away' && (
                <TimeAwayTab
                    employeeId={employee.id}
                    leaveBalances={leaveBalances}
                    leaveRequests={leaveRequests}
                    leaveSettlement={leaveSettlement}
                    companyHolidays={companyHolidays}
                    isSelf={isSelf}
                    isAdmin={isAdmin}
                    canManageTimeAway={cap?.canManageTimeAway ?? false}
                    canManageLeavePolicies={cap?.canManageLeavePolicies ?? false}
                    lang={lang}
                    requestConfirm={requestConfirm}
                    onNavigate={onNavigate}
                    onBookTimeAway={onBookTimeAway}
                    onCancelTimeAway={onCancelTimeAway}
                    onEditTimeAway={onEditTimeAway}
                    onDecideTimeAway={onDecideTimeAway}
                    onDecideCancellation={onDecideCancellation}
                    onLeaveDataChanged={onLeaveDataChanged}
                />
            )}

            {activeTabId === 'settings' && isSelf && (
                <SettingsTab
                    lang={lang}
                    onSelectLanguage={onSelectLanguage ?? (() => {})}
                    themePreference={themePreference ?? 'system'}
                    onSelectTheme={onSelectTheme ?? (() => {})}
                    themeLabels={themeLabels(lang)}
                />
            )}

            {activeTabId === 'worktime' && view && (
                <ProfileBalanceTimeTab view={view} employeeId={employee.id} lang={lang} />
            )}

            {activeTabId === 'projects' && view && (
                <ProfileProjectsTab
                    view={view}
                    userName={getDisplayName(employee)}
                    userEmail={emp?.work_email ?? null}
                />
            )}

            {activeTabId === 'documents' && (
                <DocumentFoldersSection
                    documents={documents}
                    lang={lang}
                    requestConfirm={requestConfirm}
                    onAddDocument={onAddDocument}
                    onUpdateDocument={onUpdateDocument}
                    onDeleteDocument={onDeleteDocument}
                />
            )}

            <PortalConfirmDialog
                open={pendingConfirm !== null}
                title={t.portal.shared.confirmTitle(lang)}
                message={pendingConfirm?.message ?? ''}
                confirmLabel={
                    pendingConfirm?.options?.confirmLabel ?? t.portal.shared.confirmDelete(lang)
                }
                cancelLabel={t.portal.shared.confirmCancel(lang)}
                variant={pendingConfirm?.options?.variant ?? 'danger'}
                onConfirm={() => {
                    pendingConfirm?.onConfirm()
                    setPendingConfirm(null)
                }}
                onCancel={() => setPendingConfirm(null)}
            />
        </>
    )

    const content = chromeless ? (
        tabContent
    ) : (
        <>
            <ProfileBanner
                name={getDisplayName(employee)}
                avatarUrl={employee.avatar_url}
                jobTitle={emp?.job_title}
                email={emp?.work_email}
                companies={employee.companies}
                status={employee.status}
                statusLabel={statusLabel}
                lang={lang}
                onAvatarChange={onAvatarChange}
                onAvatarRemove={onAvatarRemove}
                vcardUrl={`/api/portal/employees/${employee.id}/vcard`}
            />
            {tabStrip}
            {tabContent}
        </>
    )

    return chromeless ? content : <PageShell>{content}</PageShell>
}
